/**
 * Validation and formatting utility functions
 */

/**
 * Checks if a string is a valid URL
 * Uses URL constructor for robust validation and fallback to prefix checking
 */
export const isURL = (str: string): boolean => {
  if (!str || typeof str !== 'string') return false;
  try {
    new URL(str);
    return true;
  } catch {
    return str.startsWith('http://') || str.startsWith('https://') || str.startsWith('www.');
  }
};

/**
 * Checks if a string is a valid email address
 * Includes security checks to prevent email header injection
 */
export const isEmail = (str: string): boolean => {
  if (!str || typeof str !== 'string') return false;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(str.trim());
};

/**
 * Checks if a column name indicates it contains phone numbers
 */
export const isPhoneColumn = (columnName: string): boolean => {
  if (!columnName || typeof columnName !== 'string') return false;
  const name = columnName.toLowerCase();
  return name.includes('phone') || name.includes('tel') || name.includes('mobile') || name.includes('cell');
};

/**
 * Checks if a string contains "linkedin" (case-insensitive)
 * Works for both URLs and column names
 */
export const isLinkedIn = (str: string): boolean => {
  if (!str || typeof str !== 'string') return false;
  return str.toLowerCase().includes('linkedin');
};

/**
 * Validates email address with security checks to prevent header injection attacks
 * Use this for user inputs that will be sent to email APIs
 *
 * @param email - Email address to validate
 * @returns true if valid and safe, false otherwise
 *
 * @example
 * isValidEmailForSending("user@example.com") // true
 * isValidEmailForSending("user@example.com\nBCC: attacker@evil.com") // false
 */
export const isValidEmailForSending = (email: string): boolean => {
  if (!email || typeof email !== 'string') return false;

  // Check for email header injection attempts (newlines, carriage returns)
  // Backend validation is enforced in backend/app/schemas/gmail.py
  // Frontend check serves as additional defense-in-depth
  if (email.includes('\n') || email.includes('\r') || email.includes('\0')) {
    return false;
  }

  // Validate format
  if (!isEmail(email)) {
    return false;
  }

  // Additional length check (RFC 5321)
  if (email.length > 254) {
    return false;
  }

  return true;
};

/**
 * Checks if a value is empty (null, undefined, or empty string)
 */
export const isEmpty = (value: any): boolean => {
  return value === null || value === undefined || value === '';
};

/**
 * Checks if a value is not empty (opposite of isEmpty)
 */
export const isNotEmpty = (value: any): boolean => {
  return !isEmpty(value);
};

/**
 * Normalizes a URL by adding https:// prefix if missing
 */
export const normalizeURL = (url: string): string => {
  if (!url) return url;
  return url.startsWith('http://') || url.startsWith('https://')
    ? url
    : `https://${url}`;
};

/**
 * Checks that a URL parses and its host looks like a real domain.
 * Stricter than isURL, which accepts anything carrying an http(s) prefix.
 */
export const isWebsiteURL = (url: string): boolean => {
  if (!url || typeof url !== 'string') return false;
  try {
    const { hostname } = new URL(normalizeURL(url.trim()));
    return hostname.includes('.') && !hostname.startsWith('.') && !hostname.endsWith('.');
  } catch {
    return false;
  }
};

/**
 * Get the application URL based on environment
 * Centralized to avoid duplication across components
 */
export const getAppUrl = (): string => {
  const envUrl = import.meta.env.VITE_APP_URL;
  if (envUrl) return envUrl;

  // Fallback logic
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    return 'http://localhost:5173';
  }

  return 'https://app.skylarq.ai';
};

/**
 * Validates input length to prevent DOS attacks and buffer overflows
 *
 * @param input - Input string to validate
 * @param maxLength - Maximum allowed length (default: 10000)
 * @returns true if within limits, false otherwise
 */
export const isWithinLengthLimit = (input: string, maxLength: number = 10000): boolean => {
  if (typeof input !== 'string') return false;
  return input.length <= maxLength;
};

/**
 * Validates variable name to prevent injection and reserved keyword issues
 * Variable names must: start with letter/underscore, contain only alphanumeric/underscore
 *
 * @param varName - Variable name to validate
 * @returns true if valid, false otherwise
 *
 * @example
 * isValidVariableName("firstName") // true
 * isValidVariableName("first-name") // false
 * isValidVariableName("__proto__") // false (reserved)
 */
export const isValidVariableName = (varName: string): boolean => {
  if (!varName || typeof varName !== 'string') return false;

  // Length check
  if (varName.length > 100) return false;

  // Reserved/dangerous keywords
  const RESERVED_KEYWORDS = new Set([
    '__proto__', 'prototype', 'constructor',
    'var', 'let', 'const', 'function', 'this', 'eval'
  ]);

  if (RESERVED_KEYWORDS.has(varName.toLowerCase())) {
    return false;
  }

  // Must start with letter or underscore, followed by alphanumeric or underscore
  const varNameRegex = /^[a-zA-Z_][a-zA-Z0-9_]{0,99}$/;
  return varNameRegex.test(varName);
};

/**
 * Parse a typed recipient field ("a@x.com, Karl <karl@y.com>; b@z.com") into bare,
 * deduplicated addresses. A `Name <addr>` wrapper, as pasted from a mail client,
 * is unwrapped here rather than failing EmailStr server-side as an unexplained 422.
 * Validity is left to the API, which owns that rule.
 */
export const parseEmailAddresses = (raw: string): string[] => {
  const seen = new Set<string>();
  const out: string[] = [];
  raw.split(/[,;]/).forEach((part) => {
    const wrapped = part.match(/<([^>]+)>/);
    const addr = (wrapped ? wrapped[1] : part).trim();
    const key = addr.toLowerCase();
    if (!addr || seen.has(key)) return;
    seen.add(key);
    out.push(addr);
  });
  return out;
};
